import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useCustomers } from '../hooks/useApi';
import { toast } from 'sonner';
import { Plus, Search, Users, Pencil, Trash2, Loader2 } from 'lucide-react';

const emptyCustomer = {
  name: '',
  email: '',
  address: '',
  city: '',
  postal_code: '',
  country: '',
  tax_id: '',
  phone: ''
};

const CustomersPage = () => {
  const { getCustomers, createCustomer, updateCustomer, deleteCustomer, loading } = useCustomers();
  
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    const { data } = await getCustomers();
    if (data) setCustomers(data);
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingId(customer.id);
      setForm({
        name: customer.name,
        email: customer.email || '',
        address: customer.address || '',
        city: customer.city || '',
        postal_code: customer.postal_code || '',
        country: customer.country || '',
        tax_id: customer.tax_id || '',
        phone: customer.phone || ''
      });
    } else {
      setEditingId(null);
      setForm(emptyCustomer);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await updateCustomer(editingId, form);
        if (!error) {
          toast.success('Customer updated');
          loadCustomers();
          setDialogOpen(false);
        }
      } else {
        const { error } = await createCustomer(form);
        if (!error) {
          toast.success('Customer created');
          loadCustomers();
          setDialogOpen(false);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await deleteCustomer(deleteId);
    if (!error) {
      toast.success('Customer deleted');
      loadCustomers();
    }
    setDeleteId(null);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1">Manage your customer contacts</p>
        </div>
        <Button 
          data-testid="add-customer-btn"
          onClick={() => handleOpenDialog()}
          className="gap-2 bg-accent hover:bg-accent/90"
        >
          <Plus className="w-4 h-4" />
          Add Customer
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="customer-search"
              placeholder="Search customers..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {filteredCustomers.length} Customer{filteredCustomers.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="empty-state">
              <Users className="w-12 h-12 mb-4 opacity-50" />
              <p className="font-medium">No customers found</p>
              <p className="text-sm">
                {search ? 'Try adjusting your search' : 'Add your first customer'}
              </p>
              {!search && (
                <Button 
                  className="mt-4 gap-2" 
                  onClick={() => handleOpenDialog()}
                >
                  <Plus className="w-4 h-4" />
                  Add Customer
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Tax ID</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                      <td className="font-medium">{customer.name}</td>
                      <td className="text-muted-foreground">{customer.email || '-'}</td>
                      <td className="text-muted-foreground">{customer.phone || '-'}</td>
                      <td className="text-muted-foreground">
                        {[customer.city, customer.country].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="text-muted-foreground font-mono text-sm">
                        {customer.tax_id || '-'}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleOpenDialog(customer)}
                            data-testid={`edit-customer-${customer.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setDeleteId(customer.id)}
                            data-testid={`delete-customer-${customer.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Customer' : 'Add Customer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                data-testid="customer-form-name"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Customer name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="customer-form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  data-testid="customer-form-phone"
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+1 234 567 890"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                data-testid="customer-form-address"
                value={form.address}
                onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="Street address"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input
                data-testid="customer-form-city"
                value={form.city}
                onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))}
                placeholder="City"
              />
              <Input
                data-testid="customer-form-postal"
                value={form.postal_code}
                onChange={(e) => setForm(f => ({ ...f, postal_code: e.target.value }))}
                placeholder="Postal"
              />
              <Input
                data-testid="customer-form-country"
                value={form.country}
                onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))}
                placeholder="Country"
              />
            </div>
            <div>
              <Label>Tax ID</Label>
              <Input
                data-testid="customer-form-taxid"
                value={form.tax_id}
                onChange={(e) => setForm(f => ({ ...f, tax_id: e.target.value }))}
                placeholder="VAT / Tax ID"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              data-testid="customer-form-save"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingId ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The customer will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomersPage;
